import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Calendar, Plus, Trash2, Loader2, Power, Clock, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const AdminHolidays = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newName, setNewName] = useState('');
  const [newClosed, setNewClosed] = useState(true);
  const [newEarly, setNewEarly] = useState('');

  // Open-Overrides state
  const [overrides, setOverrides] = useState([]);
  const [showAddOverride, setShowAddOverride] = useState(false);
  const [overrideDate, setOverrideDate] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);

  useEffect(() => {
    if (!currentUser) navigate('/login');
    else if (currentUser.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [currentUser, navigate]);

  const fetchHolidays = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get('/api/holidays/');
      setHolidays(r.data || []);
    } catch (e) {
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  // Open-Overrides fetch + CRUD
  const fetchOverrides = useCallback(async () => {
    try {
      const r = await api.get('/api/open-overrides/');
      setOverrides(Array.isArray(r.data) ? r.data : []);
    } catch {
      /* silent — non-critical */
    }
  }, []);

  useEffect(() => { fetchOverrides(); }, [fetchOverrides]);

  const addOverride = async () => {
    if (!overrideDate) {
      toast.error('Pick a date');
      return;
    }
    // Client-side sanity: only Sun/Mon are eligible
    const d = new Date(overrideDate + 'T12:00:00');
    const dow = d.getDay(); // 0 Sun, 1 Mon, ..., 6 Sat
    if (dow !== 0 && dow !== 1) {
      toast.error('Open-overrides only apply to Sundays and Mondays');
      return;
    }
    setSavingOverride(true);
    try {
      await api.post('/api/open-overrides/', {
        date: overrideDate,
        reason: overrideReason.trim() || null,
      });
      toast.success(`Opened ${overrideDate} for booking`);
      setOverrideDate('');
      setOverrideReason('');
      setShowAddOverride(false);
      fetchOverrides();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add override');
    } finally {
      setSavingOverride(false);
    }
  };

  const deleteOverride = async (id) => {
    if (!window.confirm('Close this date again? Existing bookings will remain but no new bookings can be made.')) return;
    try {
      await api.delete(`/api/open-overrides/${id}`);
      toast.success('Override removed');
      fetchOverrides();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove');
    }
  };

  const seedDefaults = async () => {
    try {
      setSeeding(true);
      const r = await api.post('/api/holidays/seed-2026');
      toast.success(`Seeded ${r.data.inserted} holidays`);
      fetchHolidays();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const toggleClosed = async (h) => {
    try {
      await api.put(`/api/holidays/${h.id}`, { closed: !h.closed });
      fetchHolidays();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const toggleActive = async (h) => {
    try {
      await api.put(`/api/holidays/${h.id}`, { active: !h.active });
      fetchHolidays();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const updateEarlyClose = async (h, time) => {
    try {
      await api.put(`/api/holidays/${h.id}`, { early_close_time: time });
      fetchHolidays();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const removeHoliday = async (h) => {
    if (!window.confirm(`Remove ${h.name} (${h.date})?`)) return;
    try {
      await api.delete(`/api/holidays/${h.id}`);
      toast.success(`Removed ${h.name}`);
      fetchHolidays();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const addCustom = async () => {
    if (!newDate || !newName) {
      toast.error('Date and name are required');
      return;
    }
    try {
      await api.post('/api/holidays/', {
        date: newDate,
        name: newName,
        closed: newClosed,
        early_close_time: newClosed ? null : (newEarly || null),
      });
      toast.success(`Added ${newName}`);
      setNewDate(''); setNewName(''); setNewClosed(true); setNewEarly('');
      setShowAdd(false);
      fetchHolidays();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          size="sm"
          className="text-zinc-400 hover:text-white mb-4"
          onClick={() => navigate('/admin/dashboard')}
          data-testid="back-to-admin-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </Button>

        <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Calendar className="w-7 h-7 text-purple-400" />
              Shop Holidays
            </h1>
            <p className="text-zinc-400 mt-1 text-sm">
              Mark days as closed or half-day. Customers see &quot;Closed for [Holiday Name]&quot; when they try to book.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={seedDefaults}
              disabled={seeding}
              variant="outline"
              className="border-purple-500 text-purple-300 hover:bg-purple-600 hover:text-white"
              data-testid="seed-holidays-btn"
            >
              {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
              Seed 2026 defaults
            </Button>
            <Button
              onClick={() => setShowAdd((s) => !s)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="add-holiday-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add custom
            </Button>
          </div>
        </div>

        {showAdd && (
          <Card className="bg-zinc-900 border-zinc-800 mb-6" data-testid="add-holiday-form">
            <CardHeader>
              <CardTitle className="text-white text-lg">Add custom holiday</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Date</label>
                  <Input
                    type="date"
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Name</label>
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Anniversary closure"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newClosed}
                    onChange={(e) => setNewClosed(e.target.checked)}
                    className="w-4 h-4 accent-red-500"
                  />
                  Fully closed
                </label>
                {!newClosed && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-zinc-400" />
                    <Input
                      value={newEarly}
                      onChange={(e) => setNewEarly(e.target.value)}
                      placeholder="2:00 PM"
                      className="bg-zinc-800 border-zinc-700 text-white w-32"
                    />
                    <span className="text-xs text-zinc-500">early close time</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={addCustom} className="bg-purple-600 hover:bg-purple-700" data-testid="add-holiday-submit">
                  Save
                </Button>
                <Button onClick={() => setShowAdd(false)} variant="outline">
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white text-lg">
              {holidays.length} holidays scheduled
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12 text-zinc-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              </div>
            ) : holidays.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-400 mb-4">No holidays configured.</p>
                <Button
                  onClick={seedDefaults}
                  disabled={seeding}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Seed 2026 defaults
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {holidays.map((h) => (
                  <div
                    key={h.id}
                    className={`flex items-start gap-3 flex-wrap p-3 rounded-lg border ${
                      h.active === false ? 'bg-zinc-950 border-zinc-800 opacity-60'
                      : h.closed ? 'bg-red-950/20 border-red-900/40'
                      : 'bg-amber-950/20 border-amber-900/40'
                    }`}
                    data-testid={`holiday-row-${h.id}`}
                  >
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-white">{h.name}</span>
                        {h.closed ? (
                          <Badge className="bg-red-600 text-white text-[10px]">Closed</Badge>
                        ) : h.members_only ? (
                          <Badge className="bg-purple-600 text-white text-[10px]">Members only</Badge>
                        ) : (h.open_time || h.early_close_time) ? (
                          <Badge className="bg-amber-600 text-white text-[10px]">
                            <Clock className="w-3 h-3 mr-0.5" />
                            Limited hours
                          </Badge>
                        ) : (
                          <Badge className="bg-green-700 text-white text-[10px]">Open regular hours</Badge>
                        )}
                        {h.active === false && (
                          <Badge className="bg-zinc-700 text-zinc-300 text-[10px]">Disabled</Badge>
                        )}
                      </div>
                      <div className="text-sm text-zinc-400 mt-0.5">{formatDate(h.date)}</div>
                      {!h.closed && (
                        <div className="mt-2 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Clock className="w-3.5 h-3.5 text-zinc-400" />
                            <Input
                              value={h.open_time || ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setHolidays((prev) =>
                                  prev.map((x) => (x.id === h.id ? { ...x, open_time: v } : x))
                                );
                              }}
                              onBlur={(e) => api.put(`/api/holidays/${h.id}`, { open_time: e.target.value || null }).then(fetchHolidays).catch(() => toast.error('Update failed'))}
                              placeholder="—"
                              className="bg-zinc-800 border-zinc-700 text-white h-8 w-24 text-sm"
                            />
                            <span className="text-xs text-zinc-500">open</span>
                            <span className="text-xs text-zinc-500">→</span>
                            <Input
                              value={h.early_close_time || ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setHolidays((prev) =>
                                  prev.map((x) => (x.id === h.id ? { ...x, early_close_time: v } : x))
                                );
                              }}
                              onBlur={(e) => updateEarlyClose(h, e.target.value)}
                              placeholder="—"
                              className="bg-zinc-800 border-zinc-700 text-white h-8 w-24 text-sm"
                            />
                            <span className="text-xs text-zinc-500">close</span>
                          </div>
                          <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer w-fit">
                            <input
                              type="checkbox"
                              checked={!!h.members_only}
                              onChange={(e) => api.put(`/api/holidays/${h.id}`, { members_only: e.target.checked }).then(fetchHolidays).catch(() => toast.error('Update failed'))}
                              className="w-3.5 h-3.5 accent-purple-500"
                              data-testid={`members-only-${h.id}`}
                            />
                            Members-only (Regular & Gentleman)
                          </label>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleClosed(h)}
                        className="h-8 text-xs"
                        data-testid={`toggle-closed-${h.id}`}
                      >
                        {h.closed ? 'Allow bookings' : 'Mark fully closed'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleActive(h)}
                        className={`h-8 text-xs ${h.active === false ? 'border-green-600 text-green-400' : 'border-zinc-600 text-zinc-300'}`}
                        data-testid={`toggle-active-${h.id}`}
                      >
                        <Power className="w-3 h-3 mr-1" />
                        {h.active === false ? 'Enable' : 'Disable'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeHoliday(h)}
                        className="h-8 text-xs border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                        data-testid={`delete-holiday-${h.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ============================================================
             Open Sundays / Mondays (per-date overrides)
             ============================================================ */}
        <Card className="mt-8 bg-zinc-900 border-lime-500/30" data-testid="open-overrides-card">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <Power className="w-5 h-5 text-lime-400" />
                  Open a normally-closed day
                </CardTitle>
                <p className="text-xs text-zinc-400 mt-1">
                  Shop is normally closed Sundays &amp; Mondays. Add a date here to open it (e.g. Presidents&apos; Day, special event).
                </p>
              </div>
              <Button
                onClick={() => setShowAddOverride((v) => !v)}
                className="bg-lime-500 hover:bg-lime-600 text-black font-semibold"
                data-testid="show-add-override-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                {showAddOverride ? 'Cancel' : 'Add Open Day'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {showAddOverride && (
              <div className="mb-4 p-4 rounded-lg bg-zinc-800 border border-lime-500/20 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Date (Sun or Mon only)</label>
                  <Input
                    type="date"
                    value={overrideDate}
                    onChange={(e) => setOverrideDate(e.target.value)}
                    className="bg-zinc-900 border-zinc-700"
                    data-testid="override-date-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-zinc-400 mb-1">Reason (optional)</label>
                  <Input
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="e.g. Presidents' Day promo, Team open house"
                    className="bg-zinc-900 border-zinc-700"
                    data-testid="override-reason-input"
                  />
                </div>
                <div className="md:col-span-3 flex justify-end">
                  <Button
                    onClick={addOverride}
                    disabled={savingOverride}
                    className="bg-lime-500 hover:bg-lime-600 text-black font-semibold"
                    data-testid="save-override-btn"
                  >
                    {savingOverride ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Saving…</>
                    ) : (
                      'Open This Day'
                    )}
                  </Button>
                </div>
              </div>
            )}

            {overrides.length === 0 ? (
              <p className="text-sm text-zinc-500 py-6 text-center">
                No Sundays or Mondays are currently opened. Add one above to allow bookings on a specific closed day.
              </p>
            ) : (
              <div className="space-y-2" data-testid="open-overrides-list">
                {overrides.map((o) => (
                  <div
                    key={o.id}
                    className="flex items-center justify-between gap-3 p-3 rounded-lg bg-zinc-800/80 border border-lime-500/20"
                    data-testid={`override-row-${o.id}`}
                  >
                    <div>
                      <div className="text-white font-medium flex items-center gap-2">
                        <Badge className="bg-lime-500 text-black hover:bg-lime-500">OPEN</Badge>
                        {formatDate(o.date)}
                      </div>
                      {o.reason && (
                        <div className="text-xs text-zinc-400 mt-0.5">{o.reason}</div>
                      )}
                    </div>
                    <Button
                      onClick={() => deleteOverride(o.id)}
                      variant="outline"
                      className="h-8 text-xs border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                      data-testid={`delete-override-${o.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminHolidays;
